import React from 'react'
import { cn } from '@/lib/utils'

interface PrimaryButtonProps {
  // children is optional when `hand` is a string — the component falls back
  // to rendering `hand` as the visible label.
  children?: React.ReactNode
  hand?: boolean | string
  sub?: string
  size?: "lg" | "md"
  invert?: boolean
  disabled?: boolean
  type?: "button" | "submit" | "reset"
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  className?: string
}

export function PrimaryButton({
  children,
  hand,
  sub,
  size = "md",
  invert = false,
  disabled = false,
  type = "button",
  onClick,
  className,
}: PrimaryButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full sm:inline-flex sm:w-auto flex-col items-start gap-0.5 cursor-pointer text-left",
        "transition-[transform,box-shadow] duration-200 shadow-md",
        "rounded-[var(--radius)]",
        "hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
        size === "lg" ? "px-6 py-5" : "px-5 py-3.5",
        invert ? "bg-secondary text-primary" : "bg-primary text-background",
        className
      )}
    >
      {/* When hand prop is active, apply .hand class (Caveat font via --font-hand) */}
      {hand ? (
        <span
          className={cn(
            "hand leading-none",
            size === "lg" ? "text-3xl" : "text-2xl",
            invert ? "text-primary" : "text-secondary"
          )}
        >
          {typeof hand === "string" ? hand : children}
        </span>
      ) : (
        <span className={cn("font-semibold", size === "lg" ? "text-lg" : "text-base")}>
          {children}
        </span>
      )}
      {sub && (
        <span className="text-sm opacity-85">{sub}</span>
      )}
    </button>
  )
}
